import type { AnyGraphNode } from '../types/graphNodes';
import type { GraphDomain } from '../types/graph.types';
import { APP } from './appConfig';
import { browseHrefFor } from '../browse/facets';
import type { AppConfig, NavItem } from './types';

/**
 * Whether a node belongs to the running app. Domain is a bitmask, and many
 * nodes are stamped `All` (= 7), which includes every app. A node carrying
 * neither `domain` nor `domains` is let through, to avoid false negatives on
 * older payloads.
 *
 * The domain argument is defaulted rather than read inside the body so callers
 * (and tests) can ask about an app other than the one that was built.
 */
export const inAppDomain = (n: AnyGraphNode, domain: GraphDomain = APP.domain): boolean => {
  const d = n.domains ?? n.domain;
  if (typeof d !== 'number') return true;
  return (d & domain) !== 0;
};

const ALL_NAV: NavItem[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/browse', label: 'Browse' },
  { to: '/pronunciation', label: 'Pronunciation', englishOnly: true, feature: 'pronunciation' },
  { to: '/bhajan-babies', label: 'Bhajan Babies', feature: 'bhajanBabiesSection' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/download', label: 'Download', feature: 'appDownload' },
];

/**
 * The pronunciation guide only helps English (IAST) readers, so surface it in
 * the nav only when English is selected. Contextual links do the primary work.
 *
 * An app with no facet hubs has no /browse page at all, so its Browse item
 * points straight at the bhajan list. That decision is browseHrefFor's — the
 * same one the home page and hero CTAs read — so the nav and the CTAs cannot
 * disagree about whether /browse exists.
 */
export const navFor = (cfg: AppConfig, language: string): NavItem[] =>
  ALL_NAV
    .filter(
      (n) =>
        (!n.feature || cfg.features[n.feature]) &&
        (!n.englishOnly || language === 'English')
    )
    .map((n) => {
      if (n.to !== '/browse') return n;
      const to = browseHrefFor(cfg);
      return to === '/browse' ? n : { ...n, to, label: 'Bhajans' };
    });
